import { useEffect, useMemo, useRef, type KeyboardEvent } from "react";
import { Form, Head, Link, router, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { PageCanvas, PageHeader } from "@/components/page-canvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import {
  locationCalendarPath,
  locationJobPath,
  locationRequestPath,
  newLocationJobPath,
} from "@/routes";
import { dateTime } from "../jobs/types";
import {
  scheduleSegments,
  scheduleHour,
  slotValues,
  type ScheduleEntry,
  type ScheduleDay,
  type ScheduleSegment,
} from "@/lib/schedule";

type Bay = { id: string; name: string; enabled: boolean };
type Props = {
  date: string;
  through_date: string;
  previous_date: string;
  next_date: string;
  view: "day" | "week" | "agenda";
  time_zone: string;
  entries: ScheduleEntry[];
  days: ScheduleDay[];
  bays: Bay[];
  pagination: { page: number; pages: number };
};
function columnsFor(rows: ScheduleSegment[]) {
  const ends: number[] = [];
  return rows
    .sort(
      (a, b) =>
        a.startMinute - b.startMinute || a.entry.id.localeCompare(b.entry.id),
    )
    .map((row) => {
      let column = ends.findIndex((end) => end <= row.startMinute);
      if (column < 0) column = ends.length;
      ends[column] = row.endMinute;
      return { ...row, column };
    })
    .map((row) => ({ ...row, columns: ends.length || 1 }));
}
function moveSlotFocus(event: KeyboardEvent<HTMLAnchorElement>) {
  if (!["ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
  event.preventDefault();
  const slots = Array.from(
    event.currentTarget.parentElement!.querySelectorAll<HTMLAnchorElement>(
      "[data-slot]",
    ),
  );
  const current = slots.indexOf(event.currentTarget);
  const index =
    event.key === "Home"
      ? 0
      : event.key === "End"
        ? slots.length - 1
        : Math.max(
            0,
            Math.min(
              slots.length - 1,
              current + (event.key === "ArrowDown" ? 1 : -1),
            ),
          );
  slots.forEach(
    (slot, position) => (slot.tabIndex = position === index ? 0 : -1),
  );
  slots[index]?.focus();
}
export default function Calendar({
  date,
  through_date,
  previous_date,
  next_date,
  view,
  time_zone,
  entries,
  days,
  bays,
  pagination,
}: Props) {
  const {
    url,
    props: { auth, i18n },
  } = usePage();
  const { t } = useTranslation();
  const region = useRef<HTMLDivElement>(null);
  const query = new URLSearchParams(url.split("?")[1]);
  const requestedBay = query.get("bay");
  const selectedBay =
    bays.find((bay) => bay.id === requestedBay) ||
    bays.find((bay) => entries.some((entry) => entry.bay_id === bay.id)) ||
    bays[0];
  const segments = useMemo(
    () => scheduleSegments(entries, date, through_date, time_zone, days),
    [entries, date, through_date, time_zone, days],
  );
  useEffect(() => {
    const relevant = segments
      .filter(
        (segment) => view === "day" || segment.entry.bay_id === selectedBay?.id,
      )
      .filter((segment) => segment.startMinute > 0);
    const first = relevant.length
      ? Math.min(...relevant.map((segment) => segment.startMinute)) - 60
      : 8 * 60;
    if (region.current) region.current.scrollTop = Math.max(0, first);
  }, [date, view, selectedBay?.id, pagination.page, segments]); // Route data defines the initial viewport; user scrolling stays local.
  if (!auth.location) return null;
  const id = auth.location.id;
  const bayQuery = view === "week" ? selectedBay?.id : undefined;
  const dayLabel = (day: string, short = false) =>
    new Intl.DateTimeFormat(i18n.locale, {
      ...(short
        ? ({ weekday: "short", day: "numeric", month: "short" } as const)
        : ({ dateStyle: "full" } as const)),
      timeZone: "UTC",
    }).format(new Date(`${day}T12:00:00Z`));
  const clock = (value: string) =>
    new Intl.DateTimeFormat(i18n.locale, {
      timeZone: time_zone,
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "shortOffset",
    }).format(new Date(value));
  const source = {
    origin: "schedule",
    date,
    view: query.has("view") ? view : undefined,
    page: pagination.page > 1 ? String(pagination.page) : undefined,
    ...(query.has("bay") && bayQuery ? { bay: bayQuery } : {}),
  };
  const href = (entry: ScheduleEntry) =>
    entry.job_id
      ? locationJobPath(id, entry.job_id)
      : entry.parent?.kind === "job"
        ? locationJobPath(id, entry.parent.id, { section: "schedule" })
        : entry.parent?.kind === "request"
          ? locationRequestPath(id, entry.parent.id)
          : null;
  const entryContent = (entry: ScheduleEntry) => (
    <>
      <span className="block font-medium break-words">
        {entry.customer || t("calendar.hold")}
      </span>
      {entry.vehicle && (
        <span className="block font-medium">
          {entry.vehicle.make} {entry.vehicle.model}
          {entry.vehicle.registration_number
            ? ` · ${entry.vehicle.registration_number}`
            : ""}
        </span>
      )}
      <span className="block">{entry.bay_name}</span>
      <span className="block">
        {clock(entry.starts_at)} – {clock(entry.ends_at)}
      </span>
      <span className="block">
        {t(
          entry.kind === "hold"
            ? "calendar.hold"
            : `job_board.stages.${entry.work_state}`,
        )}
      </span>
      {entry.kind === "job" && (
        <span className="block text-muted-foreground">
          {t(`calendar_views.period.${entry.period}`)}
        </span>
      )}
      {entry.state === "in_progress" && (
        <span className="block">{t("calendar.occupied")}</span>
      )}
    </>
  );
  const create = (bay?: Bay, day?: ScheduleDay, minute?: number) =>
    newLocationJobPath(id, {
      composition: source,
      ...(bay && day && minute !== undefined
        ? { bay_id: bay.id, ...slotValues(day, minute, time_zone) }
        : {}),
    });
  const timeline = (day: ScheduleDay, bay: Bay) => {
    const duration =
      (new Date(day.ends_at).getTime() - new Date(day.starts_at).getTime()) /
      60000;
    const rows = columnsFor(
      segments.filter(
        (segment) =>
          segment.day === day.date && segment.entry.bay_id === bay.id,
      ),
    );
    const slots = Array.from(
      { length: Math.floor(duration / 60) },
      (_, hour) => hour * 60,
    ).filter(
      (minute) =>
        !rows.some(
          (row) => row.startMinute < minute + 60 && row.endMinute > minute,
        ),
    );
    const firstSlot = slots.find((minute) => minute >= 9 * 60) ?? slots[0];
    return (
      <div key={`${day.date}-${bay.id}`} className="min-w-56 border-r">
        <div className="sticky top-0 z-30 min-h-12 border-b bg-muted px-3 py-3">
          <h3 className="font-medium text-sm">
            {view === "day" ? bay.name : dayLabel(day.date, true)}
          </h3>
        </div>
        <div className="relative" style={{ height: duration }}>
          {Array.from({ length: Math.ceil(duration / 60) }, (_, hour) => {
            const tick = scheduleHour(
              new Date(
                new Date(day.starts_at).getTime() + hour * 3600000,
              ).toISOString(),
              time_zone,
            );
            const description = `${tick.iso} · ${time_zone}`;
            return (
              <div
                key={hour}
                className="pointer-events-none absolute inset-x-0 border-t border-border/60 text-[10px] text-muted-foreground"
                style={{ top: hour * 60 }}
              >
                <time
                  dateTime={tick.iso}
                  title={description}
                  aria-label={description}
                  className="relative z-10 inline-flex w-14 flex-col bg-background/90 px-1 text-[11px] leading-tight tabular-nums"
                >
                  <span aria-hidden="true">{tick.label}</span>
                  <span aria-hidden="true">{tick.offset}</span>
                </time>
              </div>
            );
          })}
          {bay.enabled &&
            slots.map((minute) => {
              const instant = new Date(
                new Date(day.starts_at).getTime() + minute * 60000,
              ).toISOString();
              const label = t("calendar_views.slot", {
                bay: bay.name,
                date: dayLabel(day.date),
                time: clock(instant),
              });
              return (
                <Link
                  key={minute}
                  data-slot={`${day.date}-${bay.id}-${minute}`}
                  tabIndex={minute === firstSlot ? 0 : -1}
                  onKeyDown={moveSlotFocus}
                  aria-keyshortcuts="ArrowUp ArrowDown Home End"
                  aria-label={label}
                  href={create(bay, day, minute)}
                  className="group absolute right-0 left-14 flex items-center justify-center text-muted-foreground/40 hover:bg-muted hover:text-foreground focus-visible:z-20 focus-visible:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
                  style={{ top: minute, height: 60 }}
                >
                  <span
                    aria-hidden="true"
                    className="rounded-full border px-2 text-lg group-hover:border-foreground/40"
                  >
                    +
                  </span>
                </Link>
              );
            })}
          {rows.map((row) => {
            const destination = href(row.entry);
            const content = (
              <div className="sticky top-14">{entryContent(row.entry)}</div>
            );
            const style = {
              top: row.startMinute,
              height: Math.max(row.endMinute - row.startMinute, 44),
              left: `calc(56px + (100% - 56px) * ${row.column / row.columns})`,
              width: `calc((100% - 56px) / ${row.columns})`,
            };
            const classes = `absolute z-10 overflow-clip rounded-sm border p-2 text-xs leading-tight focus-visible:z-20 focus-visible:ring-2 focus-visible:ring-ring ${row.entry.kind === "hold" ? "border-dashed bg-muted" : "bg-accent border-primary/40"}`;
            const label = `${row.entry.customer || t("calendar.hold")}${row.entry.vehicle ? ` · ${row.entry.vehicle.make} ${row.entry.vehicle.model} ${row.entry.vehicle.registration_number || ""}` : ""} · ${row.entry.bay_name} · ${dateTime(row.entry.starts_at, time_zone, i18n.locale)} ${clock(row.entry.starts_at)} – ${dateTime(row.entry.ends_at, time_zone, i18n.locale)} ${clock(row.entry.ends_at)}`;
            return destination ? (
              <Link
                key={row.entry.id}
                data-schedule-entry={row.entry.id}
                className={classes}
                style={style}
                href={destination}
                aria-label={label}
              >
                {content}
              </Link>
            ) : (
              <div
                key={row.entry.id}
                data-schedule-entry={row.entry.id}
                tabIndex={0}
                className={classes}
                style={style}
                aria-label={label}
              >
                {content}
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  const path = (values: Record<string, string | number | undefined> = {}) =>
    locationCalendarPath(id, { date, view, bay: bayQuery, ...values });
  return (
    <AppLayout
      breadcrumbs={[
        { title: t("calendar.title"), href: locationCalendarPath(id) },
      ]}
    >
      <Head title={t("calendar.title")} />
      <PageCanvas>
        <PageHeader
          title={t("calendar.title")}
          description={time_zone}
          actions={
            <Button render={<Link href={create()} />}>
              {t("jobs.create")}
            </Button>
          }
        />
        <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
          <Form
            action={locationCalendarPath(id)}
            method="get"
            className="flex flex-wrap items-end gap-2"
          >
            <input type="hidden" name="view" value={view} />
            {bayQuery && <input type="hidden" name="bay" value={bayQuery} />}
            <div className="space-y-1">
              <Label htmlFor="calendar-date">
                {t("calendar_views.day_date")}
              </Label>
              <Input
                id="calendar-date"
                type="date"
                name="date"
                defaultValue={date}
                required
              />
            </div>
            <Button type="submit" variant="outline">
              {t(`calendar_views.show_${view}`)}
            </Button>
          </Form>
          <nav
            aria-label={t("calendar.navigation")}
            className="flex flex-wrap gap-1"
          >
            <Button
              variant="ghost"
              render={<Link href={path({ date: previous_date })} />}
            >
              {t("customers.previous")}
            </Button>
            <Button
              variant="ghost"
              render={
                <Link
                  href={locationCalendarPath(id, { view, bay: bayQuery })}
                />
              }
            >
              {t("calendar.today")}
            </Button>
            <Button
              variant="ghost"
              render={<Link href={path({ date: next_date })} />}
            >
              {t("customers.next")}
            </Button>
          </nav>
          <nav
            className="flex flex-wrap gap-1"
            aria-label={t("calendar_views.views")}
          >
            {(["day", "week", "agenda"] as const).map((mode) => (
              <Button
                key={mode}
                variant={mode === view ? "secondary" : "ghost"}
                render={
                  <Link
                    href={locationCalendarPath(id, {
                      date,
                      view: mode,
                      bay: mode === "week" ? selectedBay?.id : undefined,
                    })}
                    aria-current={mode === view ? "page" : undefined}
                  />
                }
              >
                {t(`calendar_views.${mode}`)}
              </Button>
            ))}
          </nav>
          {view === "week" && selectedBay && (
            <div className="space-y-1">
              <Label htmlFor="calendar-bay">{t("jobs.bay_id")}</Label>
              <NativeSelect
                id="calendar-bay"
                value={selectedBay.id}
                onChange={(event) =>
                  router.get(
                    path({ bay: event.target.value }),
                    {},
                    { preserveScroll: true },
                  )
                }
              >
                {bays.map((bay) => (
                  <NativeSelectOption key={bay.id} value={bay.id}>
                    {bay.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </div>
          )}
        </div>
        {pagination.pages > 1 && (
          <div
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
            role="status"
          >
            <p className="text-sm">{t("calendar_views.partial")}</p>
            <nav
              className="flex flex-wrap items-center gap-3"
              aria-label={t("calendar.pages")}
            >
              {pagination.page > 1 && (
                <Link
                  className="min-h-11 py-2 underline"
                  href={path({ page: pagination.page - 1 })}
                >
                  {t("customers.previous")}
                </Link>
              )}
              <p>
                {t("calendar.page", {
                  page: pagination.page,
                  pages: pagination.pages,
                })}
              </p>
              {pagination.page < pagination.pages && (
                <Link
                  className="min-h-11 py-2 underline"
                  href={path({ page: pagination.page + 1 })}
                >
                  {t("customers.next")}
                </Link>
              )}
            </nav>
          </div>
        )}
        <details className="text-muted-foreground text-sm">
          <summary className="cursor-pointer">
            {t("calendar_views.notes")}
          </summary>
          <p className="mt-2">
            {t("calendar_views.capacity_help")} {t("calendar_views.slot_keys")}
          </p>
        </details>
        {!entries.length && <p>{t("calendar.empty")}</p>}
        {view === "agenda" ? (
          days.map((day) => {
            const rows = segments.filter((segment) => segment.day === day.date);
            return (
              <section key={day.date} className="space-y-3">
                <h2 className="text-section font-semibold">
                  {dayLabel(day.date)}
                </h2>
                {!rows.length && (
                  <p className="text-muted-foreground text-sm">
                    {t("calendar_views.no_entries")}
                  </p>
                )}
                <div className="grid gap-3 md:grid-cols-2">
                  {rows.map(({ entry }) => (
                    <article
                      key={entry.id}
                      data-schedule-entry={entry.id}
                      className="space-y-2 rounded-lg border bg-card p-4 text-sm"
                    >
                      <Badge
                        variant={
                          entry.kind === "hold" ? "outline" : "secondary"
                        }
                      >
                        {t(
                          entry.kind === "hold"
                            ? "calendar.hold"
                            : `job_board.stages.${entry.work_state}`,
                        )}
                      </Badge>
                      {href(entry) ? (
                        <Link className="block underline" href={href(entry)!}>
                          {entryContent(entry)}
                        </Link>
                      ) : (
                        <div>{entryContent(entry)}</div>
                      )}
                      <p>
                        {dateTime(
                          entry.starts_at,
                          entry.time_zone,
                          i18n.locale,
                        )}{" "}
                        –{" "}
                        {dateTime(entry.ends_at, entry.time_zone, i18n.locale)}
                      </p>
                      <p className="text-muted-foreground">{entry.time_zone}</p>
                      {entry.hold_expires_at && (
                        <p>
                          {t("reschedules.deadline", {
                            expiry: dateTime(
                              entry.hold_expires_at,
                              entry.time_zone,
                              i18n.locale,
                            ),
                          })}
                        </p>
                      )}
                    </article>
                  ))}
                </div>
              </section>
            );
          })
        ) : (
          <section className="space-y-3">
            <h2 className="text-section font-semibold">
              {view === "day"
                ? dayLabel(date)
                : `${dayLabel(date, true)} – ${dayLabel(through_date, true)} · ${selectedBay?.name || ""}`}
            </h2>
            <div
              ref={region}
              role="region"
              aria-label={t("calendar_views.timeline")}
              tabIndex={0}
              className="h-[min(62vh,560px)] min-h-80 overflow-auto rounded-lg border focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div
                className="grid"
                style={{
                  gridTemplateColumns: `repeat(${view === "day" ? Math.max(bays.length, 1) : days.length}, minmax(224px, 1fr))`,
                }}
              >
                {view === "day"
                  ? bays.map((bay) => timeline(days[0], bay))
                  : selectedBay
                    ? days.map((day) => timeline(day, selectedBay))
                    : null}
              </div>
            </div>
          </section>
        )}
      </PageCanvas>
    </AppLayout>
  );
}
